import Button from "../components/Button";
import { Users, Heart, Globe, ListOrdered } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import counsellor1 from "../pics/counsellor/counsellor_1.jpeg";
import counsellor2 from "../pics/counsellor/counsellor_2.jpeg";

const counselors = [
  {
    initials: "AZ",
    photo: counsellor1,
    name: "Ms. Ammara Zaidi",
    role: "Islamic Life Coaching",
    duration: "30-minute introductory session",
  },
  {
    initials: "HF",
    photo: counsellor2,
    name: "Ms. Hareem Fatima",
    role: "Clinical Psychology",
    duration: "30-minute introductory session",
  },
  {
    initials: "SA",
    photo: "",
    name:"Ms. Saher Zaidi",
    role: "Personal Development",
    duration: "30-minute introductory session",
  },
];

const steps = [
  {
    num: 1,
    title: "Book Your Session",
    body: "Schedule your first session and share your story in a safe, judgment-free space.",
  },
  {
    num: 2,
    title: "Get Your Personalized Plan",
    body: "Both counselors come together to design a healing plan built around your unique needs.",
  },
  {
    num: 3,
    title: "Begin Your Journey",
    body: "Move forward at your own pace, guided every step of the way.",
  },
];

const relationships = [
  {
    title: "Individual Counseling",
    body: "Understand yourself deeply, heal personal patterns, and grow into your best self.",
  },
  {
    title: "Family Counseling",
    body: "Rebuild trust, communication, and harmony within your family unit.",
  },
  {
    title: "Couples Counseling",
    body: "Strengthen your relationship, resolve conflicts, and rediscover your connection.",
  },
  {
    title: "Group Counseling",
    body: "Heal in community, share experiences, and grow together in a safe, guided environment.",
  },
];

const challenges = [
  {
    title: "Communication Issues",
    body: "Learn to express, listen, and truly connect with the people in your life.",
  },
  {
    title: "Intimacy Concerns",
    body: "Rebuild emotional and relational closeness with care and sensitivity.",
  },
  {
    title: "Financial Stress",
    body: "Navigate money pressures and their impact on your relationships and mental health.",
  },
  {
    title: "Trust Issues",
    body: "Heal wounds of betrayal and build healthier, stronger foundations.",
  },
  {
    title: "Life Transitions",
    body: "Manage change, loss, new beginnings, and everything in between.",
  },
  {
    title: "Divorce & Separation",
    body: "Compassionate guidance through one of life's hardest chapters, including access to legal advisory support when needed.",
  },
];

const features = [
  {
    num: "/01",
    icon: Heart,
    title: "Faith-Integrated Healing",
    body: "Islamic principles are woven naturally into every session, offering comfort, direction, and purpose alongside professional therapeutic tools.",
  },
  {
    num: "/02",
    icon: Globe,
    title: "Open For All",
    body: "Whether you are Muslim or not, our values-based approach resonates with every human heart seeking clarity, connection, and peace.",
  },
  {
    num: "/03",
    icon: ListOrdered,
    title: "Result-Driven Plans",
    body: "You won't leave without a clear roadmap. After your initial session, you receive a structured plan for how your healing journey moves forward.",
  },
];

export default function Sessions() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-black px-8 md:px-16 py-24 md:py-32 flex flex-col items-center text-center">
        <h1 className="text-9xl md:text-9xl font-modum font-bold text-brand tracking-wide leading-tight max-w-4xl">
          COUNSELING &amp; THERAPY SESSIONS
        </h1>

        <p className="mt-6 text-xl md:text-4xl font-modum text-brand tracking-wide max-w-3xl">
          Where Islamic Wisdom Meets Professional Psychology
        </p>

        <p className="mt-6 text-base md:text-2xl text-white max-w-2xl leading-relaxed">
          You don't have to carry it alone. Real healing begins when the right
          guidance meets the right heart.
        </p>

        <Button
          onClick={() =>
            window.open(
              "https://docs.google.com/forms/d/e/1FAIpQLSc0InFGhO-YsUvOJ_Ls2p6a2qZ-8T_IEKZXVtv1_dSt5_wzAA/viewform?usp=publish-editor",
              "_blank",
              "noopener,noreferrer"
            )
          }
          className="mt-10 bg-brand text-black text-lg hover:bg-white hover:text-black active:bg-black active:text-brand transition-all duration-300"
        >
          Book Your Session now!
        </Button>
      </section>

      {/* Why Our Sessions Are Unique */}
      <section className="bg-[#faf7f2] px-8 md:px-16 py-20">
        {/* Two-column intro: heading/body on left, counselors swiper on right */}
        <div className="flex flex-col xl:flex-row items-center gap-10 mb-16">
          {/* LEFT — heading + body */}
          <div className="flex-1">
            <h2 className="text-3xl md:text-5xl font-modum font-bold mb-4 tracking-wide">
              Why Our Sessions Are Unique
            </h2>
            <h3 className="border bg-brand text-black border-black py-2 mb-6 w-fit px-4 text-2xl font-modum">
              WE DON'T JUST COUNSEL, WE GUIDE YOU COMPLETELY
            </h3>
            <p className="text-gray-700 text-justify mb-6 leading-relaxed max-w-2xl">
              At Al Noor EdTech, we believe true healing touches the soul, the
              mind, and the life you live every day. Our sessions combine
              professional clinical psychology with Islamic teachings, because
              Islam is a complete way of living, not just a religion.
            </p>
            <p className="italic text-gray-700 leading-relaxed max-w-2xl">
              Together, they assess your needs from both dimensions and craft a
              personalized healing plan tailored specifically for you.
            </p>
          </div>

          {/* RIGHT — Counselors swiper */}
          <div className="flex-1 w-full min-w-0">
            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              spaceBetween={20}
              slidesPerView={1}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              style={{
                "--swiper-navigation-color": "#000",
                "--swiper-pagination-color": "#000",
              }}
              className="rounded-lg pb-10"
            >
              {counselors.map((c, i) => (
                <SwiperSlide key={i}>
                  <div className="bg-white rounded-xl shadow-md p-8 flex flex-col items-center text-center">
                    {c.photo ? (
                      <img
                        src={c.photo}
                        alt={c.name}
                        className="w-32 h-32 rounded-full object-cover mb-4"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mb-4 text-3xl font-modum font-bold text-gray-600">
                        {c.initials}
                      </div>
                    )}
                    <h4 className="font-modum font-bold text-xl mb-2 tracking-wide">
                      {c.name}
                    </h4>
                    <p className="inline-block bg-black text-brand text-sm font-medium px-3 py-1 rounded-full mb-2">
                      {c.role}
                    </p>
                    <p className="text-gray-600 text-sm">{c.duration}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

      </section>

      {/* Features section with yellow background */}
      <section className="bg-brand px-8 md:px-16 py-20">
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-md overflow-hidden grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 border-2 border-black">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.num} className="p-8 md:p-10">
                <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-2xl font-modum font-bold mb-4 tracking-wide">
                  {f.title}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {f.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>
 {/* How It Works */}
      <section className="bg-white px-8 md:px-16 py-20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-modum font-bold mb-4 tracking-wide">
            How It Works
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-16">
            Your Journey in 3 Simple Steps
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {steps.map((s) => (
              <div key={s.num} className="flex flex-col items-center text-center">
                {/* Numbered circle */}
                <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center text-2xl font-modum font-bold text-black bg-white">
                  {s.num}
                </div>

                {/* Vertical line */}
                <div className="w-px h-14 bg-black my-4" />

                <h3 className="text-3xl font-modum font-bold mb-3 tracking-wide">
                  {s.title}
                </h3>
                <p className="text-gray-600 text-mm leading-relaxed max-w-xs">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
       {/* Relationships Counselling */}
       <section className="bg-black  px-8 md:px-16 py-20">
        <div className="flex flex-col xl:flex-row items-start gap-12 ">
          {/* LEFT — heading + body */}
          <div className="flex-1">
            <h2 className="text-3xl md:text-5xl text-white font-modum font-bold mb-3 tracking-wide">
              RELATIONSHIPS COUNSELLING
            </h2>
            <p className="italic text-lg md:text-xl text-gray-600 mb-6 text-white">
              Because every bond deserves to be nurtured
            </p>
            <p className="text-gray-700 leading-relaxed max-w-2xl text-white">
              We offer dedicated counseling across all relationship dimensions.
              Whether you are navigating personal growth, family conflict,
              relationship struggles, or seeking healing in community, our
              counselors are here to guide you every step of the way.
            </p>
          </div>

          {/* RIGHT — cards */}
          <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-5">
            {relationships.map((r) => (
              <div
                key={r.title}
                className="group bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="h-3 bg-brand" />
                <div className="p-6 flex flex-col flex-1">
                  <div className="w-12 h-12 rounded-full bg-brand/20 flex items-center justify-center mb-4">
                    <Users className="w-5 h-5 text-black" />
                  </div>
                  <h3 className="font-modum font-bold text-2xl mb-3 tracking-wide">
                    {r.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {r.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Common Dynamics */}
      <section className="bg-[#faf7f2] px-8 md:px-16 py-20">
        <div className="flex flex-col xl:flex-row items-start gap-12">
          {/* LEFT — heading + body */}
          <div className="flex-1 xl:sticky xl:top-24">
            <h2 className="text-3xl md:text-5xl font-modum font-bold mb-3 tracking-wide">
              CHALLENGES &amp; COMMON DYNAMICS
            </h2>
            <p className="italic text-lg md:text-xl text-gray-600 mb-6">
              Because life throws things at us that we were never taught to handle
            </p>
            <p className="text-gray-700 leading-relaxed max-w-2xl">
              Our counselors are equipped to guide you through some of life's
              most difficult and sensitive challenges, with professionalism,
              compassion, and a deep respect for your personal journey.
            </p>
            <p className="text-gray-700 leading-relaxed max-w-2xl mt-6">
            Book one-on-one sessions with our experts. Our team will review your details and get back to you with the next steps.  
            </p>
            <Button
              onClick={() =>
                window.open(
                  "https://docs.google.com/forms/d/e/1FAIpQLSc0InFGhO-YsUvOJ_Ls2p6a2qZ-8T_IEKZXVtv1_dSt5_wzAA/viewform?usp=publish-editor",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              className="mt-6 bg-brand text-black hover:bg-black hover:text-brand transition-all duration-300"
            >
              Register Now!
            </Button>
           
          </div>

          {/* RIGHT — cards */}
          <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-5">
            {challenges.map((c) => (
              <div
                key={c.title}
                className="group bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="h-3 bg-black" />
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-modum font-bold text-2xl mb-3 tracking-wide">
                    {c.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
